import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../auth/useAuthStore'
import { getPortfolioSnapshots } from '../api/portfolioHistory'
import { getPortfolio } from '../api/trading'
import { pickStartOfDayEquity } from '../lib/equitySnapshot'

/**
 * Equity at the start of today (prior-day close snapshot, or first snapshot
 * of the day). Matches the "% acct" denominator used by the /trades day-strip
 * — so any "% of account" stat anchored to today's realized P/L stays
 * consistent across the app. Falls back to live equity when no snapshot is
 * available (brand-new account, snapshots disabled, etc.).
 */
export function useStartOfDayEquity(): number | null {
  const accountId = useAuthStore((s) => s.accountId)

  const snapshotsQ = useQuery({
    queryKey: ['portfolio-snapshots', accountId, '1W'],
    queryFn: () => getPortfolioSnapshots(accountId!, '1W'),
    enabled: !!accountId,
    staleTime: 60_000,
  })

  const portfolioQ = useQuery({
    queryKey: ['portfolio-sod-fallback', accountId],
    queryFn: getPortfolio,
    enabled: !!accountId,
    staleTime: 60_000,
  })

  const dayStartMs = (() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  })()

  const fromSnapshot = (() => {
    const pts = snapshotsQ.data?.points
    if (!pts || pts.length === 0) return null
    const v = pickStartOfDayEquity(pts, dayStartMs)
    return v != null && Number.isFinite(v) && v > 0 ? v : null
  })()

  if (fromSnapshot != null) return fromSnapshot

  const liveEquity = portfolioQ.data?.equity?.[0]?.value
  const live = liveEquity != null ? Number(liveEquity) : null
  return live != null && Number.isFinite(live) && live > 0 ? live : null
}
